import {
  type ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/use-debounce';
import {
  NeonButton,
  NeonCheckboxSingle,
  NeonLoadingIndicator,
  NeonSearch,
  NeonSelectField,
  NeonTag,
  type MenuOption,
} from '@ps-refarch-ux/neon';
import { FormPageShell } from '@/components/FormPageShell/form-page-shell';
import { MultiSectionFormLayout } from '@/components/MultiSectionFormLayout/multi-section-form-layout';
import { NeonButtonTypes } from '@/constants/neon-constants';
import { STAFF_ROUTES } from '@/constants/staff-routes';
import { useToastNotifications } from '@/hooks/use-toast-notifications';
import { useGetCourseCatalog } from '@/repositories/course-repository';
import {
  useGetRecommendationsByCourse,
  useGetRecommendationStudents,
  useGetRecommendedStudentDetails,
  useSaveRecommendation,
  type CourseRecommendationStudentItem,
  type RecommendationStudent,
} from '@/repositories/recommendations-repository';
import { CourseStatus, type Course } from '@/types';
import { getHomeUrl, getStaffRoutePath } from '@/utils/cp-context';
import { getCurrentGraduatingClassYear } from '../PlanOfStudy/create-plan-form-validation';
import { getGradeFromClassYear } from '@/utils/get-grade-from-class-year';
import styles from './recommendation-form-page.module.css';
import './recommendation-form-page.styles.css';
import coursePickerStyles from './recommendation-form-page.styles.css?inline';

type PickerStudent = RecommendationStudent;

const CLASS_YEAR_OPTION_COUNT = 7;
const COURSE_PICKER_FILTER_INPUT_ID = 'recommendation-course-popper-searchable-select-input-field';
const COURSE_PICKER_FILTER_DEBOUNCE_MS = 300;
const COURSE_PICKER_SELECTOR = 'neon-3_1_0-item-picker#recommendation-course-popper';
const COURSE_PICKER_STYLE_ATTRIBUTE = 'data-recommendation-course-picker-styles';

interface CourseItemPickerElement extends HTMLElement {
  menuOptions: MenuOption[];
  filterString: string;
}

function studentLabel(student: PickerStudent): string {
  if (student.firstName && student.lastName) {
    return `${student.lastName}, ${student.firstName}`;
  }
  return student.lastName || student.studentId || student.id;
}

/** Neon add/edit recommendation form with course and student selection. */
export function RecommendationFormPage(): ReactElement {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(courseId);
  const [selectedCourseId, setSelectedCourseId] = useState(courseId ?? '');
  const [coursePickerSearch, setCoursePickerSearch] = useState(courseId ?? '');
  const debouncedCoursePickerSearch = useDebouncedValue(
    coursePickerSearch,
    COURSE_PICKER_FILTER_DEBOUNCE_MS
  );
  const [selectedCourseSnapshot, setSelectedCourseSnapshot] = useState<Course | undefined>();
  const [classYear, setClassYear] = useState<string>('');
  const [availableSearch, setAvailableSearch] = useState('');
  const [checkedAvailableIds, setCheckedAvailableIds] = useState<Set<string>>(new Set());
  const [checkedSelectedIds, setCheckedSelectedIds] = useState<Set<string>>(new Set());
  const [initialSelectedIds, setInitialSelectedIds] = useState<Set<string>>(new Set());
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const [knownStudentsMap, setKnownStudentsMap] = useState<Map<string, PickerStudent>>(new Map());
  const courseFieldRef = useRef<HTMLDivElement>(null);
  const [coursePickerContent, setCoursePickerContent] = useState<HTMLElement | null>(null);
  const isCoursePickerLoadingRef = useRef(false);
  const activeCoursePickerRef = useRef<HTMLElement | null>(null);
  const currentGraduatingClassYear = getCurrentGraduatingClassYear();

  const { showToast } = useToastNotifications();
  const { mutate: saveRecommendation, isPending: isSaving } = useSaveRecommendation();

  const { data: existingRecommendationData } = useGetRecommendationsByCourse(
    courseId ?? '',
    isEditMode
  );
  const recommendedStudentIds = useMemo(
    () => existingRecommendationData?.students.map((student) => student.id) ?? [],
    [existingRecommendationData?.students]
  );
  const {
    data: recommendedStudents,
    isFetching: isRecommendedStudentsFetching,
    isError: isRecommendedStudentsError,
  } = useGetRecommendedStudentDetails(recommendedStudentIds);

  const syncCoursePicker = useCallback((): void => {
    const rootNode = courseFieldRef.current?.getRootNode();
    const root = rootNode instanceof ShadowRoot ? rootNode : document;
    const picker =
      root.querySelector<HTMLElement>(COURSE_PICKER_SELECTOR) ??
      document.querySelector<HTMLElement>(COURSE_PICKER_SELECTOR);
    if (picker !== activeCoursePickerRef.current) {
      activeCoursePickerRef.current?.removeAttribute('aria-busy');
      activeCoursePickerRef.current = picker;
    }
    if (!picker) {
      setCoursePickerContent(null);
      return;
    }
    const content = picker.querySelector<HTMLElement>('article') ?? picker;
    // Neon portals the popper outside the embed shadow root, so the hiding rules must live inside
    // it. Injecting imperatively keeps them present the moment Neon creates or replaces the popper.
    if (!content.querySelector(`style[${COURSE_PICKER_STYLE_ATTRIBUTE}]`)) {
      const styleElement = document.createElement('style');
      styleElement.setAttribute(COURSE_PICKER_STYLE_ATTRIBUTE, '');
      styleElement.textContent = coursePickerStyles;
      content.prepend(styleElement);
    }
    if (isCoursePickerLoadingRef.current) {
      picker.setAttribute('aria-busy', 'true');
    } else {
      picker.removeAttribute('aria-busy');
    }
    setCoursePickerContent(content);
  }, []);

  useEffect(() => {
    const rootNode = courseFieldRef.current?.getRootNode();
    const root = rootNode instanceof ShadowRoot ? rootNode : document;
    // Neon creates or replaces the detached popper outside React's render cycle.
    const observer = new MutationObserver(syncCoursePicker);
    observer.observe(root, { childList: true, subtree: true });
    if (root !== document) {
      observer.observe(document, { childList: true, subtree: true });
    }
    syncCoursePicker();
    return () => {
      observer.disconnect();
      activeCoursePickerRef.current?.removeAttribute('aria-busy');
    };
  }, [syncCoursePicker]);

  useEffect(() => {
    const handleCoursePickerInput = (event: Event): void => {
      const filterInput = event
        .composedPath()
        .find(
          (target): target is HTMLInputElement =>
            target instanceof HTMLInputElement && target.id === COURSE_PICKER_FILTER_INPUT_ID
        );
      if (filterInput) {
        // Hide Neon's locally filtered "No results" before it can paint; the layout effect
        // reconciles this with the real loading state after React renders.
        isCoursePickerLoadingRef.current = true;
        syncCoursePicker();
        setCoursePickerSearch(filterInput.value);
      }
    };

    const handleCoursePickerClear = (event: Event): void => {
      if (
        event
          .composedPath()
          .some(
            (target) =>
              target instanceof HTMLElement &&
              target.id === 'recommendation-course-popper-searchable-select-clear-button'
          )
      ) {
        setCoursePickerSearch('');
      }
    };
    // Capture inside the embed's shadow root before the browser retargets Neon input events.
    const root = courseFieldRef.current?.getRootNode() ?? document;
    const roots = root === document ? [document] : [root, document];
    roots.forEach((eventRoot) => {
      eventRoot.addEventListener('input', handleCoursePickerInput, true);
      eventRoot.addEventListener('click', handleCoursePickerClear, true);
    });
    return () => {
      roots.forEach((eventRoot) => {
        eventRoot.removeEventListener('input', handleCoursePickerInput, true);
        eventRoot.removeEventListener('click', handleCoursePickerClear, true);
      });
    };
  }, [syncCoursePicker]);

  const isSearchActive = availableSearch.trim().length > 0;
  const courseSearch = debouncedCoursePickerSearch.trim().toLowerCase();
  const activeCoursePickerSearch = coursePickerSearch.trim().toLowerCase();
  const shouldSearchCourseCatalog = courseSearch.length > 0;
  const {
    data: courseData,
    isFetching: isCourseCatalogFetching,
    isError: isCourseCatalogError,
  } = useGetCourseCatalog({
    status: CourseStatus.ACTIVE,
    sortBy: 'id',
    sortOrder: 'asc',
    page: 1,
    pageSize: 100,
    ...(shouldSearchCourseCatalog ? { search: courseSearch } : {}),
  });
  const isCoursePickerLoading =
    isCourseCatalogFetching || coursePickerSearch !== debouncedCoursePickerSearch;
  const { data: studentData, isError: isStudentError } = useGetRecommendationStudents(
    {
      page: 1,
      pageSize: 1000,
      ...(classYear ? { classYear: Number(classYear) } : {}),
    },
    Boolean(selectedCourseId && classYear)
  );
  const {
    data: studentSearchData,
    isFetching: isStudentSearchFetching,
    isError: isStudentSearchError,
  } = useGetRecommendationStudents(
    { page: 1, pageSize: 1000, search: availableSearch.trim() },
    Boolean(selectedCourseId && isSearchActive)
  );

  // Initialize edit mode selected students when recommendation details load
  useEffect(() => {
    if (isEditMode && existingRecommendationData && !isInitialized) {
      const ids = new Set(
        existingRecommendationData.students.map((s: CourseRecommendationStudentItem) =>
          String(s.id)
        )
      );
      setInitialSelectedIds(ids);
      setSelectedStudentIds(ids);
      setIsInitialized(true);
    }
  }, [isEditMode, existingRecommendationData, isInitialized]);

  // Keep a map of student metadata from the students API
  useEffect(() => {
    const students = [
      ...(studentData?.data ?? []),
      ...(studentSearchData?.data ?? []),
      ...(recommendedStudents ?? []),
    ];
    if (students.length > 0) {
      setKnownStudentsMap((prev: Map<string, PickerStudent>) => {
        const next = new Map(prev);
        for (const s of students) {
          next.set(s.id, s);
        }
        return next;
      });
    }
  }, [studentData?.data, studentSearchData?.data, recommendedStudents]);

  const courses = useMemo(() => courseData?.data ?? [], [courseData?.data]);
  const visibleCourses = useMemo(() => {
    if (!activeCoursePickerSearch) {
      return courses;
    }
    return courses.filter(
      (course: Course) =>
        course.name.toLowerCase().includes(activeCoursePickerSearch) ||
        course.id.toLowerCase().includes(activeCoursePickerSearch)
    );
  }, [activeCoursePickerSearch, courses]);
  const courseInCurrentResults = useMemo(
    () => visibleCourses.find((course: Course) => course.id === selectedCourseId),
    [selectedCourseId, visibleCourses]
  );
  const selectedCourse = courseInCurrentResults ?? selectedCourseSnapshot;
  useEffect(() => {
    if (courseInCurrentResults) {
      setSelectedCourseSnapshot(courseInCurrentResults);
    }
  }, [courseInCurrentResults]);
  const courseOptions = useMemo(
    () => [
      ...(selectedCourseId && !courseInCurrentResults
        ? [
            {
              id: selectedCourseId,
              value: selectedCourseId,
              text:
                selectedCourse?.name ??
                existingRecommendationData?.courseName ??
                searchParams.get('courseName') ??
                selectedCourseId,
              subtext: selectedCourseId,
            },
          ]
        : []),
      ...visibleCourses.map((course: Course) => ({
        id: course.id,
        value: course.id,
        // Neon only filters option text, not subtext; include the ID while searching.
        text:
          activeCoursePickerSearch && !course.name.toLowerCase().includes(activeCoursePickerSearch)
            ? `${course.name} (${course.id})`
            : course.name,
        subtext:
          activeCoursePickerSearch && !course.name.toLowerCase().includes(activeCoursePickerSearch)
            ? undefined
            : course.id,
      })),
    ],
    [
      courseInCurrentResults,
      existingRecommendationData?.courseName,
      searchParams,
      selectedCourse,
      selectedCourseId,
      visibleCourses,
      activeCoursePickerSearch,
    ]
  );
  const handleCourseSelect = useCallback(
    (value: string | undefined): void => {
      const nextCourseId = value ?? '';
      setSelectedCourseId(nextCourseId);
      setSelectedCourseSnapshot(courses.find((course) => course.id === nextCourseId));
      setCoursePickerSearch('');
    },
    [courses]
  );

  useLayoutEffect(() => {
    // Layout timing applies or clears the busy state before the browser paints this render.
    isCoursePickerLoadingRef.current = isCoursePickerLoading;
    syncCoursePicker();
  }, [coursePickerSearch, isCoursePickerLoading, syncCoursePicker]);

  useEffect(() => {
    const root = courseFieldRef.current?.getRootNode();
    const pickerRoot = root instanceof ShadowRoot ? root : document;
    const selector = 'neon-3_1_0-item-picker#recommendation-course-popper';
    const picker =
      pickerRoot.querySelector<CourseItemPickerElement>(selector) ??
      document.querySelector<CourseItemPickerElement>(selector);
    if (!picker) {
      return;
    }
    // Neon 3.1 replaces its detached popper when options change, leaving the open list stale.
    // Bind the same results through the visible item picker's public menuOptions API.
    picker.filterString = coursePickerSearch;
    picker.menuOptions = courseOptions.map((option, index) => ({
      id: `recommendation-course-option-${index}`,
      text: option.text,
      subtext: option.subtext,
      value: option.value,
      controlType: 'button',
      isSelected: () => option.value === selectedCourseId,
      onClickCallback: () => {
        courseFieldRef.current?.querySelector<HTMLButtonElement>('[role="combobox"]')?.click();
        handleCourseSelect(option.value);
      },
    }));
  }, [courseOptions, coursePickerSearch, handleCourseSelect, selectedCourseId]);

  const gradeOptions = Array.from(
    { length: CLASS_YEAR_OPTION_COUNT },
    (_, index) => currentGraduatingClassYear + index
  ).map((year) => ({
    id: String(year),
    value: String(year),
    text: `Class of ${year} (Grade ${getGradeFromClassYear(year, currentGraduatingClassYear)})`,
    labelText: `Class of ${year} (Grade ${getGradeFromClassYear(year, currentGraduatingClassYear)})`,
  }));

  const selectedStudents = useMemo(() => {
    return Array.from(selectedStudentIds).flatMap((id) => {
      const student = knownStudentsMap.get(id);
      return student ? [student] : [];
    });
  }, [selectedStudentIds, knownStudentsMap]);

  const availableStudents = useMemo(() => {
    if (!classYear) {
      return [];
    }
    return (studentData?.data ?? []).filter((student: PickerStudent) => {
      if (selectedStudentIds.has(student.id)) {
        return false;
      }
      return classYear === String(student.classYear);
    });
  }, [studentData?.data, selectedStudentIds, classYear]);
  const studentSearchResults = useMemo(() => {
    const search = availableSearch.trim().toLowerCase();
    return (studentSearchData?.data ?? []).filter(
      (student) =>
        !selectedStudentIds.has(student.id) &&
        (studentLabel(student).toLowerCase().includes(search) ||
          `${student.firstName} ${student.lastName}`.toLowerCase().includes(search) ||
          student.studentId.toLowerCase().includes(search))
    );
  }, [studentSearchData?.data, selectedStudentIds, availableSearch]);

  const moveToSelected = useCallback((ids: Iterable<string>): void => {
    setSelectedStudentIds((current: Set<string>) => new Set([...current, ...ids]));
    setCheckedAvailableIds(new Set());
    setAvailableSearch('');
  }, []);
  const moveToAvailable = useCallback((ids: Iterable<string>): void => {
    const removeIds = new Set(ids);
    setSelectedStudentIds(
      (current: Set<string>) => new Set([...current].filter((id) => !removeIds.has(id)))
    );
    setCheckedSelectedIds(new Set());
  }, []);
  const toggleCheckedStudent = useCallback((studentId: string, isSelected: boolean): void => {
    const updateCheckedIds = isSelected ? setCheckedSelectedIds : setCheckedAvailableIds;
    updateCheckedIds((current: Set<string>) => {
      const next = new Set(current);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  }, []);

  const onStudentRowClick = useCallback(
    (student: PickerStudent, isSelected: boolean): void => {
      toggleCheckedStudent(student.id, isSelected);
    },
    [toggleCheckedStudent]
  );

  const title = isEditMode ? 'Edit recommendation' : 'Add recommendation';
  const returnToList = useCallback((): void => {
    void navigate(`/${STAFF_ROUTES.recommendations.list}`);
  }, [navigate]);

  const handleSave = useCallback((): void => {
    if (!selectedCourseId || selectedStudentIds.size === 0) {
      return;
    }

    const currentSelected = Array.from(selectedStudentIds);
    const deleteRequest = isEditMode
      ? Array.from(initialSelectedIds).filter((id) => !selectedStudentIds.has(id))
      : [];

    saveRecommendation(
      {
        courseId: selectedCourseId,
        putRequest: currentSelected,
        deleteRequest,
      },
      {
        onSuccess: () => {
          showToast('success', {
            heading: isEditMode
              ? 'Recommendation updated successfully'
              : 'Recommendation created successfully',
          });
          returnToList();
        },
        onError: () => {
          showToast('error', {
            heading: 'Failed to save recommendation. Please try again.',
          });
        },
      }
    );
  }, [
    selectedCourseId,
    selectedStudentIds,
    initialSelectedIds,
    isEditMode,
    saveRecommendation,
    showToast,
    returnToList,
  ]);
  const renderStudent = useCallback(
    (student: PickerStudent, isSelected: boolean): ReactElement => {
      const checkedIds = isSelected ? checkedSelectedIds : checkedAvailableIds;
      const panel = isSelected ? 'selected' : 'available';
      const label = studentLabel(student);
      return (
        <div className={styles.student} key={student.id}>
          <NeonCheckboxSingle
            id={`recommendation-${panel}-student-${student.id}`}
            dataLabelText={label}
            dataShowLabel={false}
            value={checkedIds.has(student.id)}
            onInput={() => onStudentRowClick(student, isSelected)}
          />
          <div className={styles.studentDetails}>
            <span className={styles.studentName}>{label}</span>
            <span className={styles.studentMeta}>
              {student.studentId} - Class of {student.classYear}
            </span>
          </div>
        </div>
      );
    },
    [checkedAvailableIds, checkedSelectedIds, onStudentRowClick]
  );

  return (
    <FormPageShell
      dataTestId="recommendation-form-page"
      breadcrumbs={[
        { text: 'Home', url: getHomeUrl() },
        {
          text: 'Recommendations',
          url: getStaffRoutePath(`/${STAFF_ROUTES.recommendations.list}`),
        },
        { text: title, url: '#' },
      ]}
      breadcrumbsId="recommendation-form-breadcrumbs"
      pageTitle={title}
    >
      <MultiSectionFormLayout
        contentClassName={styles.fullWidthContent}
        formClassName={`${styles.compactForm} ${styles.form}`}
        footer={{
          footerId: 'recommendation-form-footer',
          cancelButtonId: 'recommendation-cancel',
          saveButtonId: 'recommendation-save',
          cancelLabel: 'Cancel',
          saveLabel: 'Save recommendation',
          saveDisabled: !selectedCourseId || selectedStudentIds.size === 0 || isSaving,
          onCancel: returnToList,
          onSave: handleSave,
        }}
      >
        <p className={styles.intro}>
          To recommend a course to students, select the course and then select the students.
        </p>
        <div
          ref={courseFieldRef}
          className={`${styles.field} ${styles.courseField}${isEditMode ? ` ${styles.coursePickerDisabled}` : ''}`}
        >
          <NeonSelectField
            id="recommendation-course"
            dataLabelText="Course"
            dataAriaLabel="Course"
            dataPlaceholderText="Select a course"
            dataIsRequired
            dataShowFilter
            disabled={isEditMode}
            neonPopperClose={() => setCoursePickerSearch('')}
            options={courseOptions}
            modelValue={selectedCourseId || undefined}
            modelValueChange={handleCourseSelect}
          />
          {coursePickerContent &&
            isCoursePickerLoading &&
            createPortal(
              <div
                className="recommendation-course-picker-loading"
                role="status"
                aria-label="Loading courses"
              >
                <NeonLoadingIndicator dataSize="large" />
              </div>,
              coursePickerContent
            )}
          {isCourseCatalogError && <p role="alert">Unable to load courses. Please try again.</p>}
          {selectedCourse && (
            <div className={styles.tag}>
              <NeonTag
                dataIcon="user-group-3"
                dataType="cyan"
                dataText={`Grade availability for this course: ${selectedCourse.grades.join(', ')}`}
              />
            </div>
          )}
        </div>
        <hr className={styles.divider} />
        <div className={styles.field}>
          <NeonSelectField
            id="recommendation-class-year"
            dataLabelText="Student class year/grade"
            dataPlaceholderText="Select a class year/grade"
            dataIsRequired
            disabled={!selectedCourseId}
            options={gradeOptions}
            modelValue={classYear || undefined}
            modelValueChange={(value: string | undefined) => {
              setClassYear(value ?? '');
              setCheckedAvailableIds(new Set());
            }}
          />
        </div>
        <div className={styles.picker}>
          <div className={styles.panelColumn}>
            <div className={styles.panelTopRow}>
              <span className={styles.panelTitle}>Available students</span>
              <div className={styles.search}>
                <NeonSearch
                  id="recommendation-student-search"
                  dataPlaceholderText="Search student name or ID"
                  value={availableSearch}
                  neonSearchValueChange={setAvailableSearch}
                />
                {selectedCourseId && isSearchActive && (
                  <div
                    className={styles.searchResults}
                    role="region"
                    aria-label="Student search results"
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        setAvailableSearch('');
                      }
                    }}
                  >
                    {isStudentSearchError ? (
                      <p role="alert">Unable to search students. Please try again.</p>
                    ) : isStudentSearchFetching ? (
                      <p role="status">Searching students…</p>
                    ) : studentSearchResults.length === 0 ? (
                      <p role="status">No students found.</p>
                    ) : (
                      studentSearchResults.map((student) => (
                        <button
                          key={student.id}
                          type="button"
                          className={styles.searchResult}
                          onClick={() => moveToSelected([student.id])}
                        >
                          <span className={styles.studentName}>{studentLabel(student)}</span>
                          <span className={styles.studentMeta}>
                            {student.studentId} - Class of {student.classYear}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className={styles.panel} role="region" aria-label="Available students">
              <div className={styles.studentList}>
                {isStudentError && <p role="alert">Unable to load students. Please try again.</p>}
                {selectedCourseId &&
                  classYear &&
                  availableStudents.map((student: PickerStudent) => renderStudent(student, false))}
              </div>
            </div>
          </div>
          <div className={styles.actions}>
            <NeonButton
              id="recommendation-add"
              dataType={NeonButtonTypes.Secondary}
              dataText="Add"
              dataIcon="chevron-right"
              dataIconPosition="left"
              dataAriaLabel="Add selected students"
              onClick={() => moveToSelected(checkedAvailableIds)}
            />
            <NeonButton
              id="recommendation-add-all"
              dataType={NeonButtonTypes.Secondary}
              dataText="Add all"
              dataIcon="chevron-right"
              dataIconPosition="left"
              dataAriaLabel="Add all students"
              onClick={() =>
                moveToSelected(availableStudents.map((student: PickerStudent) => student.id))
              }
            />
            <NeonButton
              id="recommendation-remove"
              dataType={NeonButtonTypes.Secondary}
              dataText="Remove"
              dataIcon="chevron-left"
              dataIconPosition="left"
              dataAriaLabel="Remove selected students"
              onClick={() => moveToAvailable(checkedSelectedIds)}
            />
            <NeonButton
              id="recommendation-remove-all"
              dataType={NeonButtonTypes.Secondary}
              dataText="Remove all"
              dataIcon="chevron-left"
              dataIconPosition="left"
              dataAriaLabel="Remove all students"
              onClick={() => moveToAvailable(selectedStudentIds)}
            />
          </div>
          <div className={styles.panelColumn}>
            <div className={styles.panelTopRow}>
              <span className={styles.panelTitle}>
                Selected students
                <span
                  id="recommendation-selected-students-count"
                  className={styles.countBadge}
                  role="status"
                  aria-label={`${selectedStudentIds.size} selected students`}
                >
                  {selectedStudentIds.size}
                </span>
              </span>
            </div>
            <div className={styles.panel} role="region" aria-label="Selected students">
              <div className={styles.studentList}>
                {isRecommendedStudentsFetching && (
                  <p role="status">Loading selected student details...</p>
                )}
                {isRecommendedStudentsError && (
                  <p role="alert">Unable to load selected student details. Please try again.</p>
                )}
                {selectedStudents.map((student: PickerStudent) => renderStudent(student, true))}
              </div>
            </div>
          </div>
        </div>
      </MultiSectionFormLayout>
    </FormPageShell>
  );
}
